import {
  isAssetReadOnly,
  PermissionValues,
  useCurrentAccount,
  useWorkspaceAsset
} from '@gripp/shared-logic';
import {
  EntitySearchInput,
  FilterHeaders,
  ImageGalleryImage,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AddFilesLinksHeaderButton } from './addFilesLinksButton';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';
import { FilesContext, FilesContextType, MediaExtended, ThirdPanelMode } from './filesContext';
import { FilterPanel, FilterPanelHeaderActions } from './filesFilterPanel';
import { FileRightColumn } from './filesRightColumn';

export const AssetFiles = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceAsset, invalidateQuery } = useWorkspaceAsset(assetId ?? '');
  const { account } = useCurrentAccount();
  const canEdit = account?.hasPermission(PermissionValues.asset.update);
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [galleryImages, setGalleryImages] = useState<ImageGalleryImage[]>([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [thirdPanelMode, setThirdPanelMode] =
    useState<ThirdPanelMode>('gallery');
  const [allFiles, setAllFiles] = useState<MediaExtended[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);
  const [isThirdPanelOpen, setIsThirdPanelOpen] = useState(false);
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);

  // Open the add sheet when navigated here with that intent (e.g. from the
  // "+ Add file" button on the AssetCard files fold). Consume the flag so a
  // later back-nav to this page doesn't auto-reopen the sheet.
  useEffect(() => {
    if (location.state?.openAddSheet) {
      setIsAddSheetOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  if (!assetId) {
    return null;
  }

  const handleSearchClose = () => {
    setSearchKeyword('');
  };

  const handleSearch = async (keyword: string) => {
    setSearchKeyword(keyword);
  };

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: <AssetCard assetId={assetId} />,
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => {
      navigate(`/assets`);
    },
    disableScroll: false,
  };

  const contextValue: FilesContextType = {
    galleryImages,
    setGalleryImages,
    galleryInitialIndex,
    setGalleryInitialIndex,
    thirdPanelMode,
    setThirdPanelMode,
    allFiles,
    selectedCategoryId,
    setSelectedCategoryId,
    isThirdPanelOpen,
    setIsThirdPanelOpen,
    isAddSheetOpen,
    setIsAddSheetOpen,
  };

  const rightColumn = {
    title: 'asset.files.title',
    mainComponent: (
      <FilesContext.Provider value={contextValue}>
        <FileRightColumn
          assetId={assetId}
          searchKeyword={searchKeyword}
          selectedCategoryId={selectedCategoryId}
          onAllFilesLoaded={setAllFiles}
        />
      </FilesContext.Provider>
    ),
    headerComponent: (
      <FilesContext.Provider value={contextValue}>
        <AssetFilesHeader
          onSearchClose={handleSearchClose}
          onFilterPress={() => {
            setThirdPanelMode('filter');
            setIsThirdPanelOpen(true);
          }}
          searchKeyword={searchKeyword}
          onSearch={handleSearch}
          showAddButton={!!(workspaceAsset?.asset && !isReadOnlyMode)}
          canEdit={!!canEdit}
        />
      </FilesContext.Provider>
    ),
    showBackButton: true,
    disableScroll: true,
    onBackButtonPress: () => {
      navigate(`/assets/${assetId}`);
    },
    backButtonTransition: SlideAction.PREV,
  };

  const thirdColumnDef = isThirdPanelOpen
    ? {
        title: `Filter (${allFiles.length})`,
        mainComponent: (
          <FilesContext.Provider value={contextValue}>
            <FilterPanel />
          </FilesContext.Provider>
        ),
        headerComponent: (
          <FilterPanelHeaderActions
            onReset={() => setSelectedCategoryId(undefined)}
          />
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsThirdPanelOpen(false),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumnDef}
      viewMode={ViewMode.DUAL}
    />
  );
};

export type AssetFilesHeaderProps = {
  onSearchClose: () => void;
  onFilterPress: () => void;
  searchKeyword: string;
  onSearch: (keyword: string) => Promise<void>;
  showAddButton: boolean;
  canEdit: boolean;
};

export const AssetFilesHeader = ({
  onSearchClose,
  onFilterPress,
  searchKeyword,
  onSearch,
  showAddButton,
  canEdit,
}: AssetFilesHeaderProps) => (
  <View style={styles.headerActions}>
    <FilterHeaders
      onSearchClose={onSearchClose}
      onFilterPress={onFilterPress}
      currentSearchKeyword={searchKeyword}
      searchComponent={
        <EntitySearchInput
          filter={{ keyword: searchKeyword }}
          onSearch={onSearch}
          placeholder="Search files..."
        />
      }
      Routes={{}}
    />
    {showAddButton && <AddFilesLinksHeaderButton disabled={!canEdit} />}
  </View>
);

const styles = StyleSheet.create({
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
});
